import {
  useSecuritiesContext,
  useWSFilterPipe,
  wsScanToMap,
  type IPredictionOutcomeEvent,
  type MinimalSubscriptionResponse,
  type Security,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { map, type Observable } from 'rxjs';
import { isPredictionOutcomeAvailableOnMarket } from '../../../PredictionMarkets/PredictionCard/predictionCardMarkets';
import type { PriceGridSourceItem } from '../../types';
import { matchesPredictionMarketsPriceGridSourceItemFilters } from './filters';
import { buildPredictionMarketsPriceGridRows, type PredictionMarketsPriceGridRow } from './rows';
import type { PredictionMarketsPriceGridFilterState } from './types';

function getSecuritySymbol(security: Security) {
  return security.Symbol;
}

export function usePredictionMarketsPriceGridRows({
  eventsByID,
  filterState,
  market,
  selectedSeriesIDs,
}: {
  eventsByID: Map<string, IPredictionOutcomeEvent>;
  filterState: PredictionMarketsPriceGridFilterState;
  market?: string;
  selectedSeriesIDs: Set<string> | undefined;
}): Observable<MinimalSubscriptionResponse<PredictionMarketsPriceGridRow>> {
  const { securityMemoryObs } = useSecuritiesContext();
  const sourceObservable = useMemo(
    () => securityMemoryObs.pipe(map(({ latestMessage }) => latestMessage)),
    [securityMemoryObs]
  );
  const marketFilterFunc = useCallback(
    (security: Security) => isPredictionOutcomeAvailableOnMarket(security, market),
    [market]
  );
  const marketFilterPipe = useWSFilterPipe({
    getUniqueKey: getSecuritySymbol,
    filterFunc: marketFilterFunc,
  });
  const filterFunc = useCallback(
    (security: Security) =>
      matchesPredictionMarketsPriceGridSourceItemFilters({
        sourceItem: { security } satisfies PriceGridSourceItem,
        eventsByID,
        filterState,
        selectedSeriesIDs,
      }),
    [eventsByID, filterState, selectedSeriesIDs]
  );
  const filterPipe = useWSFilterPipe({
    getUniqueKey: getSecuritySymbol,
    filterFunc,
  });

  return useMemo(
    () =>
      sourceObservable.pipe(
        marketFilterPipe,
        filterPipe,
        wsScanToMap({ getUniqueKey: getSecuritySymbol, newMapEachUpdate: false }),
        map(
          (securitiesBySymbol): MinimalSubscriptionResponse<PredictionMarketsPriceGridRow> => ({
            initial: true,
            data: buildPredictionMarketsPriceGridRows({
              securities: securitiesBySymbol.values(),
              eventsByID,
            }),
          })
        )
      ),
    [eventsByID, filterPipe, marketFilterPipe, sourceObservable]
  );
}
